// © Thibault Sabathier, 2026. frontend/src/utils/messageAttachments.js
//
// Préparation des pièces jointes de MESSAGERIE avant envoi multipart.
//
// ── POURQUOI UN MODULE À PART, ET PAS `compressImages()` ───────────────────
// `utils/imageCompression.js` a été écrit pour les POSTS : sa classification
// ignore le PDF (elle le rendrait `unsupported`) et sa fonction d'ensemble
// `compressImages()` RETIRE les fichiers en échec du tableau rendu. Ici il faut
// l'inverse : la messagerie accepte les documents, et l'interface montre une
// vignette PAR fichier — donc une entrée doit survivre à son propre échec pour
// pouvoir afficher sa raison.
//
// Ce qui est réellement difficile — canvas, révocation d'ObjectURL, blob nul,
// échec de décodage — est réutilisé tel quel via `browserCompressOne`. On ne
// duplique que l'orchestration, qui diffère par nature.
//
// ── LE BACKEND RESTE L'AUTORITÉ ────────────────────────────────────────────
// Tout ce qui est fait ici est du confort : refuser tôt, compresser, prévenir.
// `messageAttachmentUpload.js` revérifie le CONTENU RÉEL (sharp / octets
// magiques) après réception. Un contrôle contourné ici ne fait jamais passer un
// fichier interdit.

import { browserCompressOne, computeBudget, MAX_TOTAL_SIZE } from './imageCompression'

export { MAX_TOTAL_SIZE }

/** Miroirs EXACTS de `services/messageAttachmentUpload.js`. */
export const MAX_FILES = 5
export const MAX_FILE_SIZE = 10 * 1024 * 1024

/**
 * Types acceptés par `ALLOWED_TYPES` côté backend.
 *
 * `image/jpg` n'y figure PAS — c'est un MIME non standard que produisent
 * certains systèmes. Il est classé `image` ici parce que la compression le
 * ré-encode en `image/jpeg`, ce que le backend accepte ; en revanche il est
 * exclu du repli sur l'original, qui serait refusé en 400.
 */
const COMPRESSIBLE_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

/**
 * Le GIF traverse SANS ré-encodage. Le passer dans un canvas l'aplatirait en
 * image fixe : l'animation disparaîtrait sans erreur ni message.
 */
const PASSTHROUGH_IMAGE_MIME = ['image/gif']

const DOCUMENT_MIME = ['application/pdf']

/** MIME que le backend accepte tels quels — condition du repli sur l'original. */
const BACKEND_ACCEPTED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  ...PASSTHROUGH_IMAGE_MIME,
  ...DOCUMENT_MIME,
]

/** Attribut `accept` du sélecteur de fichiers — même liste, format DOM. */
export const ACCEPT_ATTRIBUTE = BACKEND_ACCEPTED_MIME.join(',')

const HEIC_MIME = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']
const HEIC_EXTENSION = /\.(heic|heif)$/i

export const ERROR_FILE_TOO_LARGE = 'FILE_TOO_LARGE'
export const ERROR_UNSUPPORTED_FORMAT = 'UNSUPPORTED_FORMAT'
export const ERROR_HEIC_FORMAT = 'HEIC_FORMAT'
export const ERROR_TOO_MANY_FILES = 'TOO_MANY_FILES'
export const ERROR_TOTAL_TOO_LARGE = 'TOTAL_TOO_LARGE'

/**
 * Classe un fichier sans le lire — logique pure, testable sans DOM.
 *
 * Le HEIC est testé AVANT la taille : sur un HEIC de 12 Mo, « exportez-la en
 * JPEG » est actionnable, « elle dépasse 10 Mo » ne l'est pas.
 *
 * @param {File} file
 * @returns {'compress'|'passthrough-image'|'document'|'oversize'|'heic'|'unsupported'}
 */
export const classifyAttachment = (file) => {
  const mime = (file?.type || '').toLowerCase()
  const name = file?.name || ''

  // Certains navigateurs renvoient un `type` vide sur un HEIC — d'où l'extension.
  if (HEIC_MIME.includes(mime) || HEIC_EXTENSION.test(name)) return 'heic'

  if (typeof file?.size === 'number' && file.size > MAX_FILE_SIZE) return 'oversize'

  if (DOCUMENT_MIME.includes(mime)) return 'document'
  if (PASSTHROUGH_IMAGE_MIME.includes(mime)) return 'passthrough-image'
  if (COMPRESSIBLE_MIME.includes(mime)) return 'compress'

  return 'unsupported'
}

/** Le type d'affichage, indépendant du chemin de préparation. */
export const attachmentKindOf = (file) =>
  (file?.type || '').toLowerCase().startsWith('image/') ? 'image' : 'document'

let sequence = 0
/**
 * Identifiant local stable pour les clés React et le retrait avant envoi.
 * Le nom ne peut pas servir de clé : sélectionner deux fois « photo.jpg »
 * depuis deux dossiers est un geste banal.
 */
const nextId = () => {
  sequence += 1
  return `att-${sequence}`
}

/**
 * Prépare une sélection, en CONSERVANT une entrée par fichier soumis.
 *
 * Garanties :
 *  - l'ordre de sélection est préservé ;
 *  - un fichier en échec n'empêche jamais les autres de partir ;
 *  - aucune Promise pendante : toute branche résout ;
 *  - repli sur l'original UNIQUEMENT si le backend l'accepte, et seulement
 *    APRÈS échec de compression — jamais pour un HEIC ni un `image/jpg` ;
 *  - si la compression fait GONFLER le fichier (petit PNG déjà optimisé), on
 *    garde l'original : le but est de réduire le corps, pas de ré-encoder.
 *
 * Séquentiel et non parallèle : dix `drawImage` concurrents saturent la mémoire
 * d'un mobile. Le coût est du temps, d'où `onProgress`.
 *
 * @param {File[]} files
 * @param {object} [options]
 * @param {Function} [options.compressOne] Couture DOM (injectée en test).
 * @param {number}   [options.maxDimension]
 * @param {number}   [options.quality]
 * @param {(done: number, total: number) => void} [options.onProgress]
 * @returns {Promise<Array<{id, file, kind, name, size, status, code}>>}
 *          `status` vaut 'ready' ou 'rejected'. Une entrée 'rejected' porte
 *          `code` et n'a PAS de `file` exploitable pour l'envoi.
 */
export const prepareAttachments = async (files, options = {}) => {
  const {
    compressOne = browserCompressOne,
    maxDimension = 1200,
    quality = 0.8,
    onProgress,
  } = options

  const list = Array.isArray(files) ? files : Array.from(files || [])
  const prepared = []

  const reject = (file, code) => ({
    id: nextId(),
    file: null,
    kind: attachmentKindOf(file),
    name: file?.name || '',
    size: file?.size || 0,
    status: 'rejected',
    code,
  })

  const accept = (original, outgoing) => ({
    id: nextId(),
    file: outgoing,
    kind: attachmentKindOf(outgoing),
    name: original?.name || outgoing?.name || '',
    size: outgoing?.size || 0,
    status: 'ready',
    code: null,
  })

  for (let i = 0; i < list.length; i += 1) {
    const file = list[i]

    switch (classifyAttachment(file)) {
      case 'heic':
        prepared.push(reject(file, ERROR_HEIC_FORMAT))
        break

      case 'oversize':
        prepared.push(reject(file, ERROR_FILE_TOO_LARGE))
        break

      case 'unsupported':
        prepared.push(reject(file, ERROR_UNSUPPORTED_FORMAT))
        break

      // Un PDF n'est JAMAIS ré-encodé : le canvas le détruirait, et sa taille
      // n'est de toute façon pas dominée par des pixels.
      case 'document':
      case 'passthrough-image':
        prepared.push(accept(file, file))
        break

      case 'compress':
      default:
        try {
          const compressed = await compressOne(file, { maxDimension, quality })
          if (compressed && compressed.size < file.size) {
            prepared.push(accept(file, compressed))
          } else {
            prepared.push(accept(file, file))
          }
        } catch {
          // Échec isolé. L'original ne repart que si le backend l'accepte :
          // `image/jpg` en est exclu, et serait refusé en 400.
          if (BACKEND_ACCEPTED_MIME.includes((file?.type || '').toLowerCase())) {
            prepared.push(accept(file, file))
          } else {
            prepared.push(reject(file, ERROR_UNSUPPORTED_FORMAT))
          }
        }
        break
    }

    if (onProgress) onProgress(i + 1, list.length)
  }

  return prepared
}

/**
 * Pèse une sélection COMPLÈTE et vérifie les plafonds de lot.
 *
 * À part de `prepareAttachments` parce que les plafonds portent sur la
 * requête, pas sur le dernier lot préparé : une sélection se fait souvent en
 * plusieurs gestes (glisser deux photos, puis coller une capture). Chaque lot
 * peut tenir alors que leur somme dépasse — et c'est exactement ce chemin qui
 * ramènerait un 413 nginx, émis AVANT Express, donc sans corps JSON à afficher.
 *
 * @param {Array} entries Toutes les entrées retenues (statut 'ready').
 * @returns {{totalBytes, overBudget, tooMany, code}}
 */
export const checkSelectionLimits = (entries) => {
  const ready = (entries || []).filter((e) => e?.status === 'ready')
  const { totalBytes, overBudget } = computeBudget(
    ready.map((e) => e.file),
    MAX_TOTAL_SIZE,
  )
  const tooMany = ready.length > MAX_FILES

  return {
    totalBytes,
    overBudget,
    tooMany,
    // Le nombre prime : c'est le seul plafond que l'utilisateur peut corriger
    // sans deviner un poids.
    code: tooMany ? ERROR_TOO_MANY_FILES : overBudget ? ERROR_TOTAL_TOO_LARGE : null,
  }
}

/**
 * Extrait les fichiers d'un événement de collage.
 *
 * `clipboardData.files` est vide dans plusieurs navigateurs lors d'une copie
 * d'image depuis une autre application : seul `items` est renseigné. Les deux
 * sources sont donc lues, puis dédoublonnées.
 *
 * Un collage de TEXTE ne doit rien produire : sans le filtre `kind === 'file'`,
 * chaque Ctrl+V de texte ouvrirait une pièce jointe vide.
 */
export const filesFromClipboard = (clipboardData) => {
  if (!clipboardData) return []

  const fromItems = Array.from(clipboardData.items || [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter(Boolean)

  if (fromItems.length > 0) return fromItems
  return Array.from(clipboardData.files || [])
}

/**
 * Extrait les fichiers d'un dépôt, en ignorant les DOSSIERS.
 *
 * Un dossier déposé apparaît dans `items` avec `kind === 'file'` mais rend un
 * File de taille 0 et de type vide, que le backend refuserait avec un message
 * incompréhensible. On le distingue par `webkitGetAsEntry().isDirectory`.
 */
export const filesFromDrop = (dataTransfer) => {
  if (!dataTransfer) return []

  const items = Array.from(dataTransfer.items || [])
  if (items.length > 0) {
    return items
      .filter((item) => item.kind === 'file')
      .filter((item) => {
        const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null
        return !entry || !entry.isDirectory
      })
      .map((item) => item.getAsFile())
      .filter(Boolean)
  }

  return Array.from(dataTransfer.files || [])
}

/**
 * LA RÈGLE D'ENVOI, alignée mot pour mot sur `sendMessage` côté backend :
 *
 *   texte non vide OU au moins un fichier prêt -> valide
 *   texte vide ET aucun fichier                -> invalide
 *
 * Extraite du composant, et non laissée en ligne dans son JSX, pour UNE
 * raison : c'était le verrou. La condition d'origine portait sur le seul
 * `messageText.trim()`, donc un message ne portant que des fichiers était
 * refusé par le client alors que le backend l'accepte. Une règle inline dans
 * un composant de 1400 lignes n'est pas testable par le runner du dépôt, qui
 * n'a pas de DOM — donc rien n'aurait empêché la régression de revenir.
 *
 * `blocked` et `preparing` ne sont pas des cas de la règle mais des états
 * transitoires : on n'envoie pas une sélection qui dépasse les plafonds, ni
 * une compression encore en cours.
 */
export const canSubmitMessage = ({ text, readyCount, blocked = false, preparing = false }) => {
  if (blocked || preparing) return false
  return Boolean((text || '').trim()) || (readyCount || 0) > 0
}

/**
 * Clé i18n du message d'erreur d'envoi, dérivée du STATUT HTTP.
 *
 * Fondée sur le statut et non sur le corps : un 413 peut venir de multer
 * (JSON exploitable) OU de nginx (page HTML, aucun code applicatif). Le
 * statut est la seule information présente dans les deux cas.
 */
export const sendErrorKey = (status) => {
  if (status === 403) return 'messaging:attachments.errors.send_plan'
  if (status === 413) return 'messaging:attachments.errors.send_too_large'
  if (status === 400) return 'messaging:attachments.errors.send_rejected'
  return 'messaging:attachments.errors.send_failed'
}

/**
 * Préfixes de lecture, par contexte.
 *
 * Les deux endpoints ne partagent NI leur route NI leur règle d'autorisation :
 * la messagerie privée vérifie l'appartenance à la conversation, les groupes
 * évaluent `canReadGroup`. Servir une pièce jointe de groupe via la route
 * privée ne « marcherait pas quand même » — elle rendrait 404, puisque l'id ne
 * correspond à rien dans `message_attachments`.
 *
 * C'est le point le plus facile à recopier de travers depuis la messagerie.
 */
const ATTACHMENT_SCOPES = {
  messaging: '/messaging/attachments',
  discussions: '/discussions/attachments',
}

/**
 * Chemin RELATIF d'une pièce jointe, à préfixer par `getApiUrl()`.
 *
 * Volontairement séparé de la construction de l'URL absolue : `apiClient.js`
 * lit `window.location` au chargement du module. L'importer ici casserait les
 * tests de ce fichier, qui tournent en environnement Node sans `window`. La
 * partie testable reste donc pure, et le composant assemble l'URL.
 *
 * `scope` vaut `messaging` par défaut : les appelants existants sont
 * inchangés. Un scope inconnu LÈVE plutôt que de produire une URL silencieuse
 * — la valeur est une constante littérale aux deux points d'appel, donc une
 * erreur ici est une faute de programmation, pas une donnée d'exécution.
 */
export const attachmentPath = (id, scope = 'messaging') => {
  const prefix = ATTACHMENT_SCOPES[scope]
  if (!prefix) throw new Error(`attachmentPath: scope inconnu "${scope}"`)
  return `${prefix}/${id}`
}

/**
 * Texte d'aperçu d'un message pour la liste des conversations.
 *
 * Un message ne portant QUE des fichiers a un `content` vide : l'afficher tel
 * quel produirait une ligne blanche dans le dock, indistinguable d'une
 * conversation sans message. On qualifie donc la pièce jointe.
 *
 * Les libellés sont INJECTÉS et non construits ici : cette fonction doit
 * rester pure et testable sans i18next. L'appelant passe le résultat de `t()`.
 *
 * @param {object} message
 * @param {{photo: string, document: string}} labels
 * @returns {string}
 */
export const messagePreviewText = (message, labels = {}) => {
  const content = (message?.content || '').trim()
  if (content) return content

  const attachments = Array.isArray(message?.attachments) ? message.attachments : []
  if (attachments.length === 0) return ''

  // Une seule étiquette, même sur un lot mixte : « Photo » quand il y a au
  // moins une image, sinon « Document ». Énumérer « 2 photos et 1 document »
  // serait plus précis et illisible dans la largeur d'un aperçu.
  const hasImage = attachments.some((a) => a?.attachment_type === 'image')
  return hasImage ? labels.photo || '' : labels.document || ''
}

/** Formate un poids pour l'affichage. `Mo`/`Ko` sont universels en fr/en/es. */
export const formatFileSize = (bytes) => {
  const value = Number(bytes) || 0
  if (value < 1024) return `${value} o`
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} Ko`
  return `${(value / (1024 * 1024)).toFixed(1)} Mo`
}
